import { useEffect, useState } from 'react'
import type { NextPageContext } from 'next'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'
import { useMutation } from '@apollo/client/react'
import { qsSchema } from 'lib/zod'
import { useSnackbars } from 'settle-ui/hooks/useSnackbars/useSnackbars'
import { AuthPageLayout } from 'routes/components/AuthPageLayout/AuthPageLayout'
import { withPageErrorRecovery } from 'lib/hocs/withPageErrorRecovery/withPageErrorRecovery'
import { useDeleteTokenCookieOnMount } from '../../lib/hooks/useDeleteTokenCookieOnMount/useDeleteTokenCookieOnMount'
import { CheckEmailCard } from './components/CheckEmailCard/CheckEmailCard'
import {
  ResetPasswordRequestCard,
  type ResetPasswordRequestFormValues,
} from './components/ResetPasswordRequestCard/ResetPasswordRequestCard'
import { ForgotPasswordPage_newPasswordRequestDocument } from './ForgotPasswordPage.graphql'

interface ForgotPasswordPageProps {
  initialEmail?: string | null
}

export const ForgotPasswordPage = withPageErrorRecovery<ForgotPasswordPageProps>(({ initialEmail }) => {
  const router = useRouter()
  const { showApolloOrTextError } = useSnackbars()
  const [newPasswordRequest, newPasswordRequestState] = useMutation(ForgotPasswordPage_newPasswordRequestDocument)
  const form = useForm<ResetPasswordRequestFormValues>({ defaultValues: { email: initialEmail || '' } })
  // Holds the address the link actually went to, so the confirmation card can name it. Explicit rather
  // than `formState.isSubmitSuccessful`, which counts a handled error as success — and the confirmation
  // card offers no actions, so showing it after a failed request leaves no way to retry.
  const [sentToEmail, setSentToEmail] = useState<string | null>(null)

  const handleSubmit = form.handleSubmit(async ({ email }) => {
    try {
      await newPasswordRequest({ variables: { email } })

      setSentToEmail(email)
    } catch (err) {
      showApolloOrTextError(err)
    }
  })

  useEffect(() => {
    if ('email' in router.query) {
      const query = { ...router.query }

      delete query.email

      router.replace({ query }, undefined, { shallow: true })
    }
  }, [router])

  useDeleteTokenCookieOnMount()

  return (
    <AuthPageLayout data-testid="forgot-password-page">
      {sentToEmail ? (
        <CheckEmailCard email={sentToEmail} />
      ) : (
        <ResetPasswordRequestCard
          control={form.control}
          isLoading={newPasswordRequestState.loading}
          onSubmit={handleSubmit}
        />
      )}
    </AuthPageLayout>
  )
})

ForgotPasswordPage.getInitialProps = ({ query }: NextPageContext) => ({
  initialEmail: qsSchema.parse(query.email),
})
